/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { DatePicker, type DatePickerProps } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import type { Dayjs } from 'dayjs';
import { dateFormatString } from 'pos-client/utils/dateHelpers.ts';
import { formDayJsValue } from 'pos-client/utils/dayjs.ts';

import { extractPosFormItemProps } from './FormUtils.ts';
import {
  PosFormItem,
  type PosFormItemControlledComponentProps,
  type PosFormItemUncontrolledComponentProps,
} from './PosFormItem.tsx';

type BaseDatePickerProps<ValueType = Dayjs | Dayjs> = Omit<
  DatePickerProps<ValueType>,
  'onChange' | 'value' | 'name'
>;

type ControlledDatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  ValueType extends AnyObject = Dayjs,
> = PosFormItemControlledComponentProps<TFieldValues> &
  BaseDatePickerProps<ValueType> & {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    /**
     * Forwarded to the underlying RHF `Controller`'s `rules`. Use `rules.deps`
     * to keep cross-field error messages (defined on another field via Zod
     * `superRefine`) up-to-date — see
     * https://github.com/react-hook-form/resolvers/issues/661.
     */
    rules?: UseControllerProps<TFieldValues, FieldPath<TFieldValues>>['rules'];
    onChange?: never;
    value?: never;
  };

type UncontrolledDatePickerProps<ValueType = Dayjs | Dayjs> =
  PosFormItemUncontrolledComponentProps &
    BaseDatePickerProps<ValueType> & {
      control?: undefined;
      name?: string;
      rules?: never;
      onChange: DatePickerProps<ValueType>['onChange'];
      value?: ValueType;
    };

export type PosDatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  ValueType extends AnyObject = Dayjs,
> =
  | ControlledDatePickerProps<TFieldValues, ValueType>
  | UncontrolledDatePickerProps<ValueType>;

/**
 * @component
 * @description User date picker with React-Hook-Form integration
 * @see {@link https://ant.design/components/date-picker | AntD Date Picker Component}
 *
 * @param PosDatePickerProps.control Control field from React-Hook-Form useForm
 * @remarks If an invalid control from useForm is passed then the Input will behave as an uncontrolled component and field name will not be type-safe
 * @param PosDatePickerProps.name Name of form field
 * @example
 * ```tsx
 * // Controlled (RHF) usage:
 * const { t } = useTranslation();
 * const { control } = useForm<DiscountForm>();
 *
 * <PosDatePicker
 *   control={control}
 *   name="expiry"
 *   label={t('discounts.expiryDate')}
 * />
 *
 * // Uncontrolled (No RHF) usage:
 * const [expiryDate, setExpiryDate] = useState<dayjs.Dayjs | null>(null);
 *
 * <PosDatePicker
 *   onChange={setExpiryDate}
 *   value={expiryDate}
 * />
 * ```
 */
export const PosDatePicker = <
  TFieldValues extends FieldValues = FieldValues,
  ValueType extends AnyObject = Dayjs,
>({
  control,
  name,
  required,
  rules,
  ...props
}: PosDatePickerProps<TFieldValues, ValueType>) => {
  const isControlled = !!control;

  const { posFormItemProps, rest: datePickerProps } =
    extractPosFormItemProps(props);

  const onDateChange = (
    date: any,
    defaultOnChange: (...event: any[]) => void
  ) => defaultOnChange(formDayJsValue(date) ?? null);

  if (isControlled)
    return (
      <PosFormItem
        control={control}
        name={name}
        required={required}
        {...posFormItemProps}
      >
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field: { onChange, value, ...field } }) => (
            <DatePicker
              {...datePickerProps}
              {...field}
              format={props.format ?? dateFormatString}
              onChange={(date) => onDateChange(date, onChange)}
              value={formDayJsValue(value) as AnyObject}
              data-testid={name}
              required={required}
              aria-required={required}
            />
          )}
        />
      </PosFormItem>
    );

  return <DatePicker {...props} />;
};
